import React, { useContext } from 'react';
import { Link, generatePath } from 'react-router-dom';
import {
  Button,
  Tag,
  Modal,
  Tooltip,
  Typography
} from 'antd';
import { QuestionCircleFilled, CloseCircleOutlined } from '@ant-design/icons';
import { ProblemResponse, ProblemSetNestedResponse } from '../../api/problems';
import { getNRandomProblems } from '../../utils/problems';
import { DIFFICULTY_MAP, PROBLEM_ROUTE } from '../../config/constants';
import ProblemTestcaseProgress from '../ProblemTestcaseProgress/ProblemTestcaseProgress';
import { ThemeContext, ThemeContextState } from '../../context/ThemeContext';
import styles from './ProblemResultsModal.module.css';

export interface ProblemResultsModalProps {
  isOpen: boolean
  isTestcase: boolean
  outcome: boolean
  passedTestcases: number
  totalTestcases: number
  failingTestcase: string
  expectedOutput: string
  computedOutput: string
  stdout: string
  stderr: string
  efficiencyScore: number | null
  problem: ProblemResponse | null
  problemSets: ProblemSetNestedResponse[]
  handleClose: (e: React.MouseEvent<HTMLElement>) => void
};

// modal to display problem submission results
const ProblemResultsModal = (props: ProblemResultsModalProps): JSX.Element | null => {
  // get current theme from theme context
  const { theme } = useContext(ThemeContext) as ThemeContextState;

  const handleCloseWrapper = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    props.handleClose(e);
  };

  return (
    <Modal
      className={theme === 'light' ? 'problemModalLight' : 'problemModalDark'}
      title={
        <Typography.Text
          className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
        >
          Results
        </Typography.Text>
      }
      open={props.isOpen}
      closable={false}
      footer={[
        <Button
          key='close'
          onClick={handleCloseWrapper}
          type='primary'
          className='secondaryButtonDark'
        >
          <CloseCircleOutlined />
          Close
        </Button>
      ]}
      bodyStyle={{overflowY: 'auto', maxHeight: 'calc(100vh - 300px)'}}
    >
      <div>
        <ProblemTestcaseProgress
          passedTestcases={props.passedTestcases}
          totalTestcases={props.totalTestcases}
        />
        {!props.outcome &&
          <div className={styles.outcomeContainer}>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              Failing testcase
            </Typography.Text>
            <pre
              className={`${styles.preblock} ${theme === 'light' ? styles.preblockLight : styles.preblockDark}`}
            >
              {props.failingTestcase}
            </pre>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              Expected
            </Typography.Text>
            <pre
              className={`${styles.preblock} ${theme === 'light' ? styles.preblockLight : styles.preblockDark}`}
            >
              {props.expectedOutput}
            </pre>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              Computed
            </Typography.Text>
            <pre
              className={`${styles.preblock} ${theme === 'light' ? styles.preblockLight : styles.preblockDark}`}
            >
              {props.computedOutput}
            </pre>
          </div>
        }
        {props.outcome && props.efficiencyScore && props.efficiencyScore >= 0 && !props.isTestcase &&
          <div className={styles.efficiencyOutcomeContainer}>
            <Typography.Text
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              <strong>Efficiency score</strong>&nbsp;&nbsp;
              <Tooltip
                placement='rightTop'
                title='This represents how efficient your solution is. Less than 1 means there is room for improvement, close to 1 means it is as efficient as the accepted optimal solution. If you consistently get a number significantly higher than 1, then congrats, you just beat our optimal solution!'
              >
                <QuestionCircleFilled className={styles.tooltipIcon} />
              </Tooltip>
            </Typography.Text>
            <Tag color={props.efficiencyScore < 0.5 ? 'red' : props.efficiencyScore < 0.85 ? 'orange' : 'green'}>
              {props.efficiencyScore}
            </Tag>
          </div>
        }
        {props.outcome && !props.isTestcase &&
          <div className={styles.successOutcomeContainer}>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              Well done!
            </Typography.Text>
            <p style={{lineHeight: 2}}>
              {getNRandomProblems(
                props.problemSets.flatMap((ps: ProblemSetNestedResponse) => ps.problems
                ).filter((p: ProblemResponse) => !p.is_solved && p.id !== props.problem?.id),
                3
              ).map((p: ProblemResponse) => (
                <Link key={p.id.toString()} reloadDocument to={generatePath(PROBLEM_ROUTE, { key: p.slug ? p.slug : p.id.toString() })}>
                  <Tag className={DIFFICULTY_MAP[p.difficulty].tagClassSecondary}>
                    {p.title}
                  </Tag>
                </Link>
              ))}
            </p>
          </div>
        }
        {!props.outcome && props.stdout?.length > 0 &&
          <div className={styles.stdoutOutcomeContainer}>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              stdout
            </Typography.Text>
            <pre
              className={`${styles.preblock} ${theme === 'light' ? styles.preblockLight : styles.preblockDark}`}
            >
              {props.stdout}
            </pre>
          </div>
        }
        {!props.outcome && props.stderr?.length > 0 &&
          <div className={styles.stderrOutcomeContainer}>
            <Typography.Text
              strong={true}
              className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
            >
              stderr
            </Typography.Text>
            <pre
              className={`${styles.preblock} ${theme === 'light' ? styles.preblockLight : styles.preblockDark}`}
            >
              {props.stderr}
            </pre>
          </div>
        }
      </div>
    </Modal>
  );
};

export default ProblemResultsModal;
